import React, { useState, useEffect, useCallback } from 'react';
import { fetchDigestData, digestDispatch } from '../services/api';

// ── i18n ──
const i18n = {
  en: {
    header: 'Daily Digest',
    tomorrow_label: "Tomorrow's checkouts",
    checkouts_label: 'Checkouts',
    upcoming: 'Upcoming checkouts',
    staying: 'Currently staying',
    assign: 'Assign →',
    dispatching: 'Dispatching…',
    assigned_label: 'Assigned ✓',
    no_cleaners: 'No cleaners',
    unassigned: 'UNASSIGNED',
    expired: 'This link has expired.',
    expired_sub: 'Check WhatsApp for today\'s digest.',
    invalid: 'This link is invalid.',
    no_token: 'No access token provided.',
    loading: 'Loading your digest…',
    no_properties: 'No properties found.',
    no_checkouts: 'No checkouts scheduled.',
    assigned_count: 'assigned',
    unassigned_count: 'unassigned',
    staying_label: 'STAYING',
    blocked_label: 'BLOCKED',
    live: 'live · refreshes every 5 min',
  },
  es: {
    header: 'Reporte Diario',
    tomorrow_label: 'Salidas de mañana',
    checkouts_label: 'Salidas',
    upcoming: 'Próximas salidas',
    staying: 'Hospedados actualmente',
    assign: 'Asignar →',
    dispatching: 'Enviando…',
    assigned_label: 'Asignado ✓',
    no_cleaners: 'Sin limpiadores',
    unassigned: 'SIN ASIGNAR',
    expired: 'Este enlace ha expirado.',
    expired_sub: 'Revisa WhatsApp para el reporte de hoy.',
    invalid: 'Este enlace no es válido.',
    no_token: 'No se proporcionó token.',
    loading: 'Cargando tu reporte…',
    no_properties: 'No se encontraron propiedades.',
    no_checkouts: 'Sin salidas programadas.',
    assigned_count: 'asignados',
    unassigned_count: 'sin asignar',
    staying_label: 'HOSPEDADO',
    blocked_label: 'BLOQUEADO',
    live: 'en vivo · actualiza cada 5 min',
  },
};

const POLL_INTERVAL = 5 * 60 * 1000;

// Format "15:00" → "3pm", "11:00" → "11am"
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const sfx = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${sfx}` : `${h12}:${String(m).padStart(2, '0')}${sfx}`;
}

// ── Inject Google Fonts ──
if (!document.getElementById('dd-fonts')) {
  const link = document.createElement('link');
  link.id = 'dd-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700&family=DM+Mono:wght@400;500&family=Outfit:wght@300;400;500;600&display=swap';
  document.head.appendChild(link);
}

// ── Inject CSS ──
if (!document.getElementById('dd-styles')) {
  const s = document.createElement('style');
  s.id = 'dd-styles';
  s.textContent = `
/* ── Reset ── */
.dd-container * { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }

/* ── Container ── */
.dd-container {
  background: #0f0f0f;
  color: #f0ede8;
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
  min-height: 100vh;
  max-width: 430px;
  margin: 0 auto;
}

/* ── Header ── */
.dd-header {
  padding: 48px 20px 16px;
  background: #0f0f0f;
  border-bottom: 1px solid #2a2a2a;
  position: sticky;
  top: 0;
  z-index: 10;
}
.dd-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.dd-brand {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 0.95rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #c8a96e;
}
.dd-header-title {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 2rem;
  font-weight: 600;
  margin-top: 4px;
  line-height: 1.2;
  color: #f0ede8;
}
.dd-header-date {
  font-family: 'DM Mono', monospace;
  font-size: 0.9rem;
  color: #666;
  text-align: right;
  line-height: 1.5;
}
.dd-header-summary {
  display: flex;
  gap: 16px;
  margin-top: 12px;
}
.dd-sum-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95rem;
  color: #888;
  font-family: 'DM Mono', monospace;
}
.dd-dot {
  width: 9px; height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dd-dot-green { background: #4ade80; }
.dd-dot-red   { background: #f87171; }

/* ── Property ── */
.dd-property { padding: 20px 16px 0; }
.dd-property-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.dd-property-name {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: #c8a96e;
  letter-spacing: 0.05em;
}
.dd-property-city {
  font-size: 0.9rem;
  color: #666;
  font-family: 'DM Mono', monospace;
  margin-top: 2px;
}
.dd-property-badge {
  font-size: 0.82rem;
  font-family: 'DM Mono', monospace;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid #2a2a2a;
  color: #666;
  flex-shrink: 0;
}
.dd-property-badge.dd-alert {
  border-color: #f87171;
  color: #f87171;
  background: #2a0f0f;
}
.dd-property-divider {
  height: 1px;
  background: #2a2a2a;
  margin: 20px 16px 0;
}

/* ── Section label ── */
.dd-section-label {
  font-size: 0.82rem;
  font-family: 'DM Mono', monospace;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin: 14px 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.dd-section-label::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #2a2a2a;
}

/* ── Unit card ── */
.dd-unit-card {
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 14px;
  margin-bottom: 8px;
  overflow: hidden;
  animation: dd-fadeUp 0.3s ease both;
}
.dd-unit-card.dd-card-unassigned {
  border-color: #3a1a1a;
  background: #1c1414;
}
.dd-unit-row {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 12px;
}
.dd-unit-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
  font-weight: 700;
  line-height: 1;
}
.dd-unit-info { flex: 1; min-width: 0; }
.dd-unit-name {
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #f0ede8;
}
.dd-unit-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  flex-wrap: wrap;
}
.dd-meta-chip {
  font-size: 0.85rem;
  font-family: 'DM Mono', monospace;
  color: #666;
}
.dd-meta-chip.dd-cleaner { color: #4ade80; }
.dd-meta-chip.dd-unassigned-text { color: #f87171; }
.dd-meta-chip.dd-staying-text { color: #818cf8; }
.dd-meta-chip.dd-time { color: #c8a96e; }
.dd-meta-sep {
  color: #2a2a2a;
  font-size: 0.85rem;
}

/* ── Assign button ── */
.dd-assign-btn {
  background: #2a0f0f;
  border: 1px solid #5a2020;
  color: #f87171;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Outfit', sans-serif;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
  letter-spacing: 0.03em;
}
.dd-assign-btn:active { transform: scale(0.95); }
.dd-assign-btn.dd-dispatching {
  background: #1a1a0f;
  border-color: #4a4a20;
  color: #fbbf24;
  animation: dd-blink 1.2s infinite;
}
.dd-assign-btn.dd-success {
  background: #0f2a1a;
  border-color: #1a4a2a;
  color: #4ade80;
}
.dd-assign-btn.dd-failed {
  background: #1a1a1a;
  border-color: #2a2a2a;
  color: #666;
  cursor: default;
}

/* ── Footer ── */
.dd-footer {
  padding: 24px 16px 48px;
  text-align: center;
}
.dd-footer-brand {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.15rem;
  color: #c8a96e;
  display: block;
  margin-bottom: 4px;
}
.dd-footer-meta {
  font-size: 0.85rem;
  color: #666;
  font-family: 'DM Mono', monospace;
}
.dd-refresh-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.82rem;
  color: #666;
  font-family: 'DM Mono', monospace;
  margin-bottom: 12px;
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  padding: 6px 14px;
  border-radius: 20px;
}
.dd-pulse-dot {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: #4ade80;
  animation: dd-pulse 2s infinite;
}

/* ── Empty / Error / Loading ── */
.dd-empty {
  text-align: center;
  padding: 24px 16px;
  color: #555;
  font-size: 1rem;
  font-family: 'DM Mono', monospace;
}
.dd-error-container, .dd-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  padding: 24px;
  text-align: center;
}
.dd-error-icon { font-size: 2.5rem; margin-bottom: 16px; opacity: 0.6; }
.dd-error-title {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.1rem;
  color: #f0ede8;
  font-weight: 600;
  margin-bottom: 8px;
}
.dd-error-sub {
  font-size: 0.75rem;
  color: #666;
  font-family: 'DM Mono', monospace;
}
.dd-spinner {
  width: 28px; height: 28px;
  border: 2px solid #2a2a2a;
  border-top-color: #c8a96e;
  border-radius: 50%;
  animation: dd-spin 0.8s linear infinite;
}
.dd-loading-text {
  color: #666;
  font-size: 1rem;
  margin-top: 14px;
  font-family: 'DM Mono', monospace;
}

/* ── Animations ── */
@keyframes dd-fadeUp {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes dd-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.4; transform: scale(0.8); }
}
@keyframes dd-spin { to { transform: rotate(360deg); } }
@keyframes dd-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.5; }
}
`;
  document.head.appendChild(s);
}

// ── Main Component ──
const DigestDashboard = () => {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dispatchingUids, setDispatchingUids] = useState({});
  const [lang, setLang] = useState('en');

  const t = i18n[lang] || i18n.en;

  // Dark theme: set body background + mobile theme-color
  useEffect(() => {
    document.body.style.background = '#0f0f0f';
    document.body.style.margin = '0';
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = '#0f0f0f';
    return () => { document.body.style.background = ''; };
  }, []);

  const loadData = useCallback(async (showLoading = false) => {
    if (!token) { setError('no_token'); setLoading(false); return; }
    if (showLoading) setLoading(true);
    try {
      const response = await fetchDigestData(token);
      const result = response.data;
      setData(result);
      setLang(result.admin?.language || 'en');
      setError(null);
    } catch (err) {
      const errType = err.response?.data?.error;
      setError(errType === 'expired' ? 'expired' : 'invalid');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(true); }, [loadData]);

  useEffect(() => {
    if (error || !token) return;
    const iv = setInterval(() => loadData(false), POLL_INTERVAL);
    return () => clearInterval(iv);
  }, [error, token, loadData]);

  const handleDispatch = async (propertyId, reservation) => {
    const uid = reservation.reservation_uid;
    setDispatchingUids(prev => ({ ...prev, [uid]: 'dispatching' }));
    try {
      await digestDispatch(token, {
        property_id: propertyId,
        reservation_uid: uid,
        check_out: reservation.checkout_date_iso,
        check_in: reservation.checkin_date_iso,
      });
      setTimeout(() => loadData(false), 10000);
    } catch {
      setDispatchingUids(prev => ({ ...prev, [uid]: 'error' }));
    }
  };

  // ── Error state ──
  if (error) {
    return (
      <div className="dd-container">
        <div className="dd-error-container">
          <div className="dd-error-icon">{error === 'expired' ? '⏱' : '🔒'}</div>
          <div className="dd-error-title">
            {error === 'expired' ? t.expired : (error === 'no_token' ? t.no_token : t.invalid)}
          </div>
          {error === 'expired' && <div className="dd-error-sub">{t.expired_sub}</div>}
        </div>
      </div>
    );
  }

  // ── Loading state ──
  if (loading || !data) {
    return (
      <div className="dd-container">
        <div className="dd-loading-container">
          <div className="dd-spinner" />
          <div className="dd-loading-text">{t.loading}</div>
        </div>
      </div>
    );
  }

  const properties = data.properties || [];

  // Compute summary from all visible reservations (tomorrow + upcoming)
  let totalAssigned = 0;
  let totalUnassigned = 0;
  for (const p of properties) {
    for (const r of (p.checkouts_tomorrow || [])) {
      if (r.status === 'assigned') totalAssigned++; else if (r.status === 'unassigned') totalUnassigned++;
    }
    for (const r of (p.upcoming_checkouts || [])) {
      if (r.status === 'assigned') totalAssigned++; else if (r.status === 'unassigned') totalUnassigned++;
    }
  }

  return (
    <div className="dd-container">
      {/* ── Header ── */}
      <div className="dd-header">
        <div className="dd-header-top">
          <div>
            <div className="dd-brand">AYNI</div>
            <div className="dd-header-title">{t.header}</div>
          </div>
          <div className="dd-header-date">
            <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '2px' }}>{t.tomorrow_label}</div>
            {data.date}
          </div>
        </div>
        <div className="dd-header-summary">
          <div className="dd-sum-chip"><div className="dd-dot dd-dot-green" />{totalAssigned} {t.assigned_count}</div>
          <div className="dd-sum-chip"><div className="dd-dot dd-dot-red" />{totalUnassigned} {t.unassigned_count}</div>
        </div>
      </div>

      {/* ── Properties ── */}
      {properties.length > 0 ? (
        properties.map((property, idx) => (
          <React.Fragment key={property.id}>
            {idx > 0 && <div className="dd-property-divider" />}
            <PropertySection
              property={property}
              t={t}
              dispatchingUids={dispatchingUids}
              onDispatch={handleDispatch}
            />
          </React.Fragment>
        ))
      ) : (
        <div className="dd-empty">{t.no_properties}</div>
      )}

      {/* ── Footer ── */}
      <div className="dd-footer">
        <div className="dd-refresh-chip"><div className="dd-pulse-dot" />{t.live}</div>
        <br />
        <span className="dd-footer-brand">AYNI</span>
        <div className="dd-footer-meta">{t.tomorrow_label} · {data.date}</div>
      </div>
    </div>
  );
};

// ── Property Section ──
const PropertySection = ({ property, t, dispatchingUids, onDispatch }) => {
  const checkoutsTmrw = property.checkouts_tomorrow || [];
  const upcoming = property.upcoming_checkouts || [];
  const staying = property.staying || [];
  const hasAny = checkoutsTmrw.length + upcoming.length + staying.length > 0;

  const unassignedCount =
    checkoutsTmrw.filter(r => r.status === 'unassigned').length +
    upcoming.filter(r => r.status === 'unassigned').length;

  return (
    <div className="dd-property">
      <div className="dd-property-header">
        <div>
          <div className="dd-property-name">{property.name}</div>
          <div className="dd-property-city">{property.city}</div>
        </div>
        {unassignedCount > 0 && (
          <div className="dd-property-badge dd-alert">
            {unassignedCount} {t.unassigned_count}
          </div>
        )}
      </div>

      {!hasAny && <div className="dd-empty">{t.no_checkouts}</div>}

      {checkoutsTmrw.length > 0 && (
        <>
          <div className="dd-section-label">{t.checkouts_label} {property.next_checkout_date || ''} ({checkoutsTmrw.length})</div>
          {checkoutsTmrw.map(res => (
            <ReservationCard key={res.reservation_uid} reservation={res} propertyId={property.id}
              checkoutTime={property.check_out_time} t={t}
              dispatchState={dispatchingUids[res.reservation_uid]} onDispatch={onDispatch} />
          ))}
        </>
      )}

      {staying.length > 0 && (
        <>
          <div className="dd-section-label">{t.staying} ({staying.length})</div>
          {staying.map(res => (
            <ReservationCard key={res.reservation_uid} reservation={res} propertyId={property.id}
              checkoutTime={property.check_out_time} t={t}
              dispatchState={dispatchingUids[res.reservation_uid]} onDispatch={onDispatch} />
          ))}
        </>
      )}

      {upcoming.length > 0 && (
        <>
          <div className="dd-section-label">{t.upcoming} ({upcoming.length})</div>
          {upcoming.map(res => (
            <ReservationCard key={res.reservation_uid} reservation={res} propertyId={property.id}
              checkoutTime={property.check_out_time} t={t}
              dispatchState={dispatchingUids[res.reservation_uid]} onDispatch={onDispatch} />
          ))}
        </>
      )}
    </div>
  );
};

// ── Reservation Card ──
const ReservationCard = ({ reservation, propertyId, checkoutTime, t, dispatchState, onDispatch }) => {
  const isBlocked = reservation.status === 'blocked' || reservation.is_blocked;
  const isAssigned = reservation.status === 'assigned';
  const isStaying = reservation.is_staying && !isBlocked;
  const isUnassigned = reservation.status === 'unassigned';

  const guestDisplay = reservation.guest || '—';

  // Icon: checkmark for assigned, diamond for staying, block for blocked, circle for unassigned
  const iconColor = isBlocked ? '#666' : isAssigned ? '#4ade80' : isStaying ? '#818cf8' : '#f87171';
  const iconChar = isBlocked ? '⊘' : isAssigned ? '✓' : isStaying ? '◆' : '●';

  // Button state
  const btnState = dispatchState || (isAssigned ? 'assigned' : 'default');
  const btnClass = btnState === 'dispatching' ? 'dd-dispatching'
    : btnState === 'assigned' ? 'dd-success'
    : btnState === 'error' ? 'dd-failed' : '';
  const btnText = btnState === 'dispatching' ? t.dispatching
    : btnState === 'assigned' ? t.assigned_label
    : btnState === 'error' ? t.no_cleaners : t.assign;

  // Cleaning date + window: "Mar 13 · 11am — 5pm"
  const coTime = fmtTime(checkoutTime);
  const ciTime = fmtTime(reservation.checkin_time);
  let dateInfo = reservation.checkout_date || '';
  if (coTime && ciTime) {
    dateInfo += ` · ${coTime} — ${ciTime}`;
  } else if (coTime) {
    dateInfo += ` · ${coTime}`;
  }

  return (
    <div className={`dd-unit-card${isUnassigned ? ' dd-card-unassigned' : ''}`}>
      <div className="dd-unit-row">
        <div className="dd-unit-icon" style={{ color: iconColor }}>{iconChar}</div>
        <div className="dd-unit-info">
          <div className="dd-unit-name">{guestDisplay}</div>
          <div className="dd-unit-meta">
            {/* Status chip: cleaner name, BLOCKED, UNASSIGNED, or STAYING */}
            {isBlocked ? (
              <span className="dd-meta-chip" style={{ color: '#666' }}>{t.blocked_label}</span>
            ) : isStaying ? (
              <span className="dd-meta-chip dd-staying-text">{t.staying_label}</span>
            ) : isAssigned ? (
              <span className="dd-meta-chip dd-cleaner">{reservation.cleaner}</span>
            ) : (
              <span className="dd-meta-chip dd-unassigned-text">{t.unassigned}</span>
            )}

            {/* Cleaning date + window */}
            <span className="dd-meta-sep">·</span>
            <span className="dd-meta-chip">{dateInfo}</span>
          </div>
        </div>

        {/* Assign button — only for unassigned, non-staying, non-blocked */}
        {isUnassigned && !isStaying && !isBlocked && (
          <button
            className={`dd-assign-btn ${btnClass}`}
            disabled={btnState !== 'default'}
            onClick={() => onDispatch(propertyId, reservation)}
          >
            {btnText}
          </button>
        )}
      </div>
    </div>
  );
};

export default DigestDashboard;
